import { datadogRum } from '@datadog/browser-rum';
import { Button, useTheme } from '@mui/material';
import { toast, useIsMobile } from '@side/pantry';
import html2canvas from 'html2canvas';
import { useState } from 'react';
import { downloadURL } from '@/common/utils/downloadURL';

const EXTRA_PADDING = 16;
export const ExportToPNGButton = ({
  chartElement,
  imageName,
  disabled = false,
}: {
  chartElement: HTMLElement | null;
  imageName: string;
  disabled?: boolean;
}) => {
  const isMobile = useIsMobile('md');
  const theme = useTheme();
  const [isExporting, setIsExporting] = useState(false);

  const handleExportToPNG = async () => {
    if (!chartElement) return;

    setIsExporting(true);
    try {
      const canvas = await html2canvas(chartElement, {
        backgroundColor: theme.palette.common.white,
        scale: 2,
        logging: process.env.NEXT_PUBLIC_APP_ENV !== 'prod',
      });
      const paddedWidth = canvas.width + EXTRA_PADDING * 2;
      const paddedHeight = canvas.height + EXTRA_PADDING * 2;
      const paddedCanvas = document.createElement('canvas');
      paddedCanvas.width = paddedWidth;
      paddedCanvas.height = paddedHeight;
      const ctx = paddedCanvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = theme.palette.common.white;
        ctx.fillRect(0, 0, paddedWidth, paddedHeight);
        ctx.drawImage(canvas, EXTRA_PADDING, EXTRA_PADDING);
      }
      const canvasToExport = ctx ? paddedCanvas : canvas;
      const dataUrl = canvasToExport.toDataURL('image/png');
      downloadURL(dataUrl, imageName);
      toast('success', 'Chart exported successfully', {
        showCloseButton: true,
      });
    } catch (err) {
      datadogRum.addError('Failed to export chart to PNG', {
        error: err,
        imageName,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant="outlined"
      color="utility"
      onClick={handleExportToPNG}
      disabled={disabled}
      loading={isExporting}
      size={isMobile ? 'small' : 'medium'}
    >
      {isMobile ? 'Export' : 'Export to PNG'}
    </Button>
  );
};
